import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCan } from "@/lib/casl/use-can";
import {
  useSettings,
  useUpdateSettings,
} from "@/lib/hooks/sms-lifecycle/use-sms-lifecycle";
import { Loader2, Save } from "lucide-react";
import { useEffect, useState } from "react";

const SETTING_DEFAULTS: Record<string, string> = {
  max_sms_per_user_per_week: "3",
  scheduler_send_time: "15:00",
  min_hours_between_sms: "36",
  send_days_default: "Mon,Tue,Wed,Thu,Fri",
};

const SCHEDULER_TIME_MIN = "06:00";
const SCHEDULER_TIME_MAX = "15:00";

export const SettingsTab = () => {
  const can = useCan();
  const canUpdate = can("update", "sms-lifecycle");

  const { data: settings, isLoading } = useSettings();
  const updateMutation = useUpdateSettings();
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Sync formData when settings load from the server.
  // Merge in defaults so they're included in the save payload, not just visual fallbacks.
  useEffect(() => {
    if (settings) {
      setFormData({ ...SETTING_DEFAULTS, ...settings });
    }
  }, [settings]);

  const handleChange = (key: string, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    await updateMutation.mutateAsync(formData);
    setHasChanges(false);
  };

  if (isLoading)
    return (
      <div className="flex h-24 w-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Settings</CardTitle>
        <CardDescription>Global SMS lifecycle configuration</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Max SMS per user per week</Label>
          <Input
            type="number"
            value={formData.max_sms_per_user_per_week ?? ""}
            onChange={(e) =>
              handleChange("max_sms_per_user_per_week", e.target.value)
            }
            disabled={!canUpdate}
          />
        </div>
        <div>
          <Label>Min hours between SMS</Label>
          <Input
            type="number"
            value={formData.min_hours_between_sms ?? ""}
            onChange={(e) =>
              handleChange("min_hours_between_sms", e.target.value)
            }
            disabled={!canUpdate}
          />
        </div>
        <div>
          <Label>Send days</Label>
          <div className="mt-2 flex gap-1.5">
            {(["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const).map(
              (day) => {
                const activeDays = (formData.send_days_default ?? "")
                  .split(",")
                  .map((d) => d.trim())
                  .filter(Boolean);
                const isActive = activeDays.includes(day);
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => {
                      const next = isActive
                        ? activeDays.filter((d) => d !== day)
                        : [...activeDays, day];
                      const ordered = [
                        "Mon",
                        "Tue",
                        "Wed",
                        "Thu",
                        "Fri",
                        "Sat",
                        "Sun",
                      ].filter((d) => next.includes(d));
                      handleChange("send_days_default", ordered.join(","));
                    }}
                    disabled={!canUpdate}
                    className={`inline-flex h-9 w-12 items-center justify-center rounded-md border text-sm font-medium transition-all ${
                      isActive
                        ? "border-primary/40 bg-primary/10 text-primary ring-1 ring-primary/20"
                        : "border-border bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                    }`}
                  >
                    {day}
                  </button>
                );
              },
            )}
          </div>
        </div>
        <div>
          <Label>Scheduler send time (24h, EST)</Label>
          <Input
            type="time"
            min={SCHEDULER_TIME_MIN}
            max={SCHEDULER_TIME_MAX}
            value={formData.scheduler_send_time ?? ""}
            onChange={(e) =>
              handleChange("scheduler_send_time", e.target.value)
            }
            disabled={!canUpdate}
          />
          {formData.scheduler_send_time &&
            (formData.scheduler_send_time < SCHEDULER_TIME_MIN ||
              formData.scheduler_send_time > SCHEDULER_TIME_MAX) && (
              <p className="mt-1 text-xs text-destructive">
                Must be between {SCHEDULER_TIME_MIN} and {SCHEDULER_TIME_MAX}{" "}
                EST
              </p>
            )}
        </div>
        <Button
          onClick={handleSave}
          disabled={
            !canUpdate ||
            !hasChanges ||
            updateMutation.isPending ||
            !!(
              formData.scheduler_send_time &&
              (formData.scheduler_send_time < SCHEDULER_TIME_MIN ||
                formData.scheduler_send_time > SCHEDULER_TIME_MAX)
            )
          }
        >
          {updateMutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Save Settings
        </Button>
      </CardContent>
    </Card>
  );
};
